import type { DbEnum } from "../../generated/prisma-enums";
import type { ModelCapability } from "../model-capability";
import { isSchemaNode, type JevSchemaRefusal, type SchemaNode } from "./jev-schema-plan.types";
import { translateSchemaToJevQuestions } from "./translate-schema-to-jev-questions";

/**
 * An AI task kind as its string value. Written as a template literal over the
 * enum so a `DbEnum` member, a Prisma row value and a plain API string all
 * assign to it without a cast.
 */
export type AITaskKindValue = `${DbEnum.AITaskKind}`;

type OutputFormatValue = `${DbEnum.AITaskOutputFormat}`;

/**
 * The capability a task of each kind needs from its model. A picker lists only
 * models of this capability, and the server refuses any other.
 */
export const KIND_CAPABILITY = {
  GENERATIVE: "generate",
  DECISION: "classify"
} as const satisfies Record<AITaskKindValue, ModelCapability>;

function isAITaskKindValue(value: unknown): value is AITaskKindValue {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(KIND_CAPABILITY, value);
}

/**
 * The kind of a task as read, with GENERATIVE for a read that carries none. A
 * task stored before kinds existed, a version snapshot taken before them, and a
 * query that does not select the column all read without a kind, and every task
 * of that time is generative. Every read that indexes a per-kind table goes
 * through here, so a missing kind never indexes one with `undefined`.
 */
export function aiTaskKindOrGenerative(kind: unknown): AITaskKindValue {
  return isAITaskKindValue(kind) ? kind : "GENERATIVE";
}

/** The parts of an AI task that decide whether its kind and its model agree. */
export interface DecisionTaskFitInput {
  /** Absent or `null` reads as GENERATIVE — see {@link aiTaskKindOrGenerative}. */
  readonly kind?: AITaskKindValue | null;
  /** The capability of the model that answers — `generate` for a custom model. */
  readonly modelCapability: ModelCapability;
  readonly outputFormat: OutputFormatValue;
  readonly multimodal: boolean;
  readonly jsonInputSchema: unknown;
  readonly jsonOutputSchema: unknown;
}

/**
 * The `field` of a refusal about a task-level setting rather than a schema field.
 * Exported so the server's own task-level refusals and the editor's rendering of
 * them read this one value.
 */
export const TASK_REFUSAL_FIELD = "(task)";

/**
 * Per kind, the task-level reason given when the model has the other capability.
 * Exported so the server can tell a refusal that is only about the model from one
 * that names the task's own fields, and advise accordingly.
 */
export const KIND_MODEL_REFUSAL_REASON = {
  GENERATIVE: "a generative task needs a model that writes",
  DECISION: "a decision task needs a decision model"
} as const satisfies Record<AITaskKindValue, string>;

/**
 * What a decision task says about each output format. `null` = it can answer it.
 * Total over the enum, so a new format does not compile until it is decided here.
 */
const DECISION_OUTPUT_FORMAT_REFUSAL = {
  JSON: null,
  TEXT: "output format TEXT asks for free text, and a decision task answers only typed questions",
  TEMPLATE:
    "output format TEMPLATE asks for free text, and a decision task answers only typed questions"
} as const satisfies Record<OutputFormatValue, string | null>;

/** The image marker of an input field: `format: "image"`, `type: "image"`, or a list of them. */
function isImageField(field: unknown): boolean {
  if (!isSchemaNode(field)) return false;
  if (field["format"] === "image" || field["type"] === "image") return true;
  return field["type"] === "array" && isImageField(field["items"]);
}

/** A stored input schema is a full object schema or a bare properties map. */
function propertiesOf(schema: SchemaNode): SchemaNode {
  const properties = schema["properties"];
  return isSchemaNode(properties) ? properties : schema;
}

/** Dotted paths of every image input field, at any depth, inside objects and lists. */
function imageFieldPaths(schema: unknown, parent: readonly string[]): string[] {
  if (!isSchemaNode(schema)) return [];
  const paths: string[] = [];
  for (const [key, child] of Object.entries(propertiesOf(schema))) {
    const path = [...parent, key];
    if (isImageField(child)) {
      paths.push(path.join("."));
      continue;
    }
    if (!isSchemaNode(child)) continue;
    if (isSchemaNode(child["properties"])) paths.push(...imageFieldPaths(child, path));
    const items = child["items"];
    if (isSchemaNode(items) && isSchemaNode(items["properties"])) {
      paths.push(...imageFieldPaths(items, path));
    }
  }
  return paths;
}

function generativeRefusals(task: DecisionTaskFitInput): readonly JevSchemaRefusal[] {
  if (task.modelCapability === KIND_CAPABILITY.GENERATIVE) return [];
  return [{ field: TASK_REFUSAL_FIELD, reason: KIND_MODEL_REFUSAL_REASON.GENERATIVE }];
}

function decisionRefusals(task: DecisionTaskFitInput): readonly JevSchemaRefusal[] {
  const refusals: JevSchemaRefusal[] = [];
  if (task.modelCapability !== KIND_CAPABILITY.DECISION) {
    refusals.push({ field: TASK_REFUSAL_FIELD, reason: KIND_MODEL_REFUSAL_REASON.DECISION });
  }
  const formatRefusal = DECISION_OUTPUT_FORMAT_REFUSAL[task.outputFormat];
  if (formatRefusal !== null) refusals.push({ field: TASK_REFUSAL_FIELD, reason: formatRefusal });
  if (task.multimodal) {
    refusals.push({
      field: TASK_REFUSAL_FIELD,
      reason: "multimodal is on, and a decision model reads text only"
    });
  }
  for (const path of imageFieldPaths(task.jsonInputSchema, [])) {
    refusals.push({
      field: path,
      reason: "is an image input, and a decision model reads text only"
    });
  }
  if (formatRefusal === null) {
    const translation = translateSchemaToJevQuestions(task.jsonOutputSchema);
    if (!translation.ok) refusals.push(...translation.refusals);
  }
  return refusals;
}

/** Per kind, every reason a task of that kind cannot be answered as configured. */
const REFUSALS_BY_KIND = {
  GENERATIVE: generativeRefusals,
  DECISION: decisionRefusals
} as const satisfies Record<
  AITaskKindValue,
  (task: DecisionTaskFitInput) => readonly JevSchemaRefusal[]
>;

/**
 * Every reason an AI task of its kind cannot be answered as configured — all of
 * them, in a stable order, never only the first. Empty when the task fits.
 *
 * One rule with two consumers: the server refuses a save with these, and the
 * editor marks the same fields while the author types. A generative task must
 * run on a model that writes. A decision task must run on a decision model,
 * answer in JSON, read text only, and ask only questions the schema translator
 * can turn into decision questions.
 *
 * A reason says why the task does not fit and never what to do: the server's
 * message adds its own advice after the list, so advice here would read twice.
 */
export function decisionTaskRefusals(task: DecisionTaskFitInput): readonly JevSchemaRefusal[] {
  return REFUSALS_BY_KIND[aiTaskKindOrGenerative(task.kind)](task);
}
